/**
 * Jotai atoms for preorders feature state management
 * Manages filter, sort, pagination, and selection state
 */
import { atom } from 'jotai';

import { PREORDERS_SERVICE_SORT } from '@ekhodealer/ekho-common';

import type { PreordersServiceSort, PreordersTableTab } from '@ekhodealer/ekho-common';
import type { SortDirection } from '@ekhodealer/ekho-common/rev';
import type { Selection } from 'react-aria-components';
import type { SearchPreordersParams } from '../api/useSearchPreorders';
import type { NumericRange } from '../types/filters';

import atomWithDebounce from '@common/atoms/atomWithDebounce';
import { getCommonDateRange } from '@common/lib/filters';
import { getEndOfDay } from '@common/lib/time';
import { CommonDateFilter } from '@common/types/filters';

import { PREORDERS_TABLE_COLUMN_ID } from '../constants/preordersTableColumns';

/**
 * Maps column ID and sort direction to PreordersServiceSort enum value
 * Used to convert UI sort state to API sort parameter
 */
function mapColumnToPreordersServiceSort(
	columnId: string,
	direction: SortDirection,
): PreordersServiceSort | undefined {
	if (direction === 'none') {
		return undefined;
	}

	const sortMap: Record<string, { asc: PreordersServiceSort; desc: PreordersServiceSort }> = {
		[PREORDERS_TABLE_COLUMN_ID.DATE_PURCHASED]: {
			asc: PREORDERS_SERVICE_SORT.DATE_PURCHASED_ASC,
			desc: PREORDERS_SERVICE_SORT.DATE_PURCHASED_DESC,
		},
		[PREORDERS_TABLE_COLUMN_ID.BUYER]: {
			asc: PREORDERS_SERVICE_SORT.BUYER_ASC,
			desc: PREORDERS_SERVICE_SORT.BUYER_DESC,
		},
		[PREORDERS_TABLE_COLUMN_ID.PREORDER_AMOUNT]: {
			asc: PREORDERS_SERVICE_SORT.ORDER_FEE_ASC,
			desc: PREORDERS_SERVICE_SORT.ORDER_FEE_DESC,
		},
	};

	const columnSort = sortMap[columnId];
	if (!columnSort) {
		return undefined;
	}

	return direction === 'ascending' ? columnSort.asc : columnSort.desc;
}

// Search value atoms with debouncing
const searchDebounceAtoms = atomWithDebounce('', 500);
export const debouncedSearchValueAtom = searchDebounceAtoms.debouncedValueAtom;
export const searchValueAtom = searchDebounceAtoms.currentValueAtom;

// Preorders table tab filter atom
export const preorderTabFilterAtom = atom<PreordersTableTab | null>(null);

// Status filter atoms (multiple selections)
export const statusFilterAtom = atom<string[]>([]);

// Model filter atoms (multiple selections)
export const modelFilterAtom = atom<string[]>([]);

// Order date filter atoms (single selection from predefined ranges)
export const orderDateFilterAtom = atom<CommonDateFilter | null>(null);

// Custom order date range (Unix ms timestamps, used when orderDateFilterAtom === CUSTOM)
export const orderDateCustomRangeAtom = atom<{ from: number | null; to: number | null }>({
	from: null,
	to: null,
});

// Preorder amount range filter
export const preorderAmountRangeAtom = atom<NumericRange>({});

// Estimated retail price range filter
export const estRetailPriceRangeAtom = atom<NumericRange>({});

// Registration state filter (single selection via dropdown)
export const registrationStateFilterAtom = atom<string | null>(null);

// Sorting configuration
export const sortConfigAtom = atom<
	Array<{
		column: string;
		direction: SortDirection;
	}>
>([]);

// Pagination atoms
export const currentPageAtom = atom<number>(1);
export const itemsPerPageAtom = atom<number>(50);

// Selection atoms
export const selectedPreordersAtom = atom<Selection>(new Set<string>());
export const isAllMatchingPreordersSelectedAtom = atom<boolean>(false);

// Current preorders list atom - tracks preorder IDs from current query for navigation
export const currentPreordersListAtom = atom<string[]>([]);

// Derived atom for API parameters
export const preordersParamsAtom = atom((get): SearchPreordersParams => {
	const searchValue = get(debouncedSearchValueAtom);
	const preorderTab = get(preorderTabFilterAtom);
	const currentPage = get(currentPageAtom);
	const itemsPerPage = get(itemsPerPageAtom);

	const params: SearchPreordersParams = {
		limit: itemsPerPage,
		page: currentPage - 1, // Convert to 0-indexed for API
		facets: ['*'], // Request all facets that match the query
	};

	if (searchValue.trim()) {
		params.query = searchValue.trim();
	}

	// Build filters object
	const filters: SearchPreordersParams['filters'] = {};

	// Add preorder tab filter
	if (preorderTab) {
		filters.tabs = [preorderTab];
	}

	// Add status filters
	const statuses = get(statusFilterAtom);
	if (statuses.length > 0) {
		filters.status = statuses.length === 1 ? statuses[0] : statuses;
	}

	// Add model filters
	const models = get(modelFilterAtom);
	if (models.length > 0) {
		filters.name = models.length === 1 ? models[0] : models;
	}

	// Add order date filter (single selection or custom range)
	const commonDateFilter = get(orderDateFilterAtom);
	if (commonDateFilter) {
		if (commonDateFilter === CommonDateFilter.CUSTOM) {
			const customRange = get(orderDateCustomRangeAtom);
			if (customRange.from !== null) {
				filters.orderDateRange = {
					gte: customRange.from,
					...(customRange.to !== null ? { lte: getEndOfDay(customRange.to) } : {}),
				};
			}
		} else {
			const dateRange = getCommonDateRange(commonDateFilter);
			if (dateRange) {
				filters.orderDateRange = dateRange;
			}
		}
	}

	// Add preorder amount range filter
	const amountRange = get(preorderAmountRangeAtom);
	if (amountRange.min !== undefined || amountRange.max !== undefined) {
		filters.preorderAmountRange = amountRange;
	}

	// Add estimated retail price range filter
	const estRetailPriceRange = get(estRetailPriceRangeAtom);
	if (estRetailPriceRange.min !== undefined || estRetailPriceRange.max !== undefined) {
		filters.estRetailPriceRange = estRetailPriceRange;
	}

	// Add registration state filter
	const registrationState = get(registrationStateFilterAtom);
	if (registrationState) {
		filters.registrationState = registrationState;
	}

	if (Object.keys(filters).length > 0) {
		params.filters = filters;
	}

	// Add sorting if configured
	const sortConfig = get(sortConfigAtom);
	if (sortConfig.length > 0) {
		const sort = sortConfig[0]; // We only support single column sorting
		if (sort) {
			const sortBy = mapColumnToPreordersServiceSort(sort.column, sort.direction);
			if (sortBy) {
				params.sortBy = sortBy;
			}
		}
	}

	return params;
});

// Derived atom for base API parameters (without most filters)
// Used for getting facet counts that show total available options
// Only includes search query and preorder tab to provide context
export const preordersBaseParamsAtom = atom((get): SearchPreordersParams => {
	const searchValue = get(debouncedSearchValueAtom);
	const preorderTab = get(preorderTabFilterAtom);

	const params: SearchPreordersParams = {
		limit: 0, // We only need facets, not data
		page: 0,
		facets: ['*'], // Request all facets that match the query
	};

	if (searchValue.trim()) {
		params.query = searchValue.trim();
	}

	// Build filters object (exclude most filters for facet counts)
	const filters: SearchPreordersParams['filters'] = {};

	// Add preorder tab filter
	if (preorderTab) {
		filters.tabs = [preorderTab];
	}

	if (Object.keys(filters).length > 0) {
		params.filters = filters;
	}

	return params;
});

// Action atom to handle search value changes
export const updateSearchValueAtom = atom(
	(get) => get(searchValueAtom),
	(get, set, newValue: string) => {
		set(debouncedSearchValueAtom, newValue);
		// Reset to first page when searching
		set(currentPageAtom, 1);
	},
);

// Action atom to handle preorder tab filter changes
export const updatePreordersTableTabFilterAtom = atom(
	(get) => get(preorderTabFilterAtom),
	(get, set, newTab: PreordersTableTab | null) => {
		set(preorderTabFilterAtom, newTab);
		// Clear selection when changing tab filter
		set(selectedPreordersAtom, new Set<string>());
		set(isAllMatchingPreordersSelectedAtom, false);
		// Reset to first page when changing tab filter
		set(currentPageAtom, 1);
	},
);

// Action atom to handle items per page changes
export const updateItemsPerPageAtom = atom(
	(get) => get(itemsPerPageAtom),
	(get, set, newItemsPerPage: number) => {
		set(itemsPerPageAtom, newItemsPerPage);
		// Reset to first page when changing items per page
		set(currentPageAtom, 1);
	},
);

// Action atom to handle sort changes
export const updateSortAtom = atom(
	null,
	(get, set, payload: { columnId: string; direction: SortDirection }) => {
		const { columnId, direction } = payload;
		if (direction === 'none') {
			set(sortConfigAtom, []);
		} else {
			set(sortConfigAtom, [{ column: columnId, direction }]);
		}
		// Reset to first page when sorting changes
		set(currentPageAtom, 1);
	},
);

// Action atom to handle status filter changes
export const updateStatusFilterAtom = atom(
	(get) => get(statusFilterAtom),
	(get, set, newValue: string[]) => {
		set(statusFilterAtom, newValue);
		// Reset to first page when changing filter
		set(currentPageAtom, 1);
	},
);

// Action atom to handle model filter changes
export const updateModelFilterAtom = atom(
	(get) => get(modelFilterAtom),
	(get, set, newValue: string[]) => {
		set(modelFilterAtom, newValue);
		// Reset to first page when changing filter
		set(currentPageAtom, 1);
	},
);

// Action atom to handle order date filter changes
export const updateOrderDateFilterAtom = atom(
	(get) => get(orderDateFilterAtom),
	(get, set, newValue: CommonDateFilter | null) => {
		set(orderDateFilterAtom, newValue);
		// Clear custom range when switching away from custom
		if (newValue !== CommonDateFilter.CUSTOM) {
			set(orderDateCustomRangeAtom, { from: null, to: null });
		}
		// Reset to first page when changing filter
		set(currentPageAtom, 1);
	},
);

// Action atom to handle order date custom range changes
export const updateOrderDateCustomRangeAtom = atom(
	(get) => get(orderDateCustomRangeAtom),
	(get, set, newValue: { from: number | null; to: number | null }) => {
		set(orderDateCustomRangeAtom, newValue);
		// Reset to first page when changing filter
		set(currentPageAtom, 1);
	},
);

// Action atom to handle preorder amount range changes
export const updatePreorderAmountRangeAtom = atom(
	(get) => get(preorderAmountRangeAtom),
	(get, set, newValue: NumericRange) => {
		set(preorderAmountRangeAtom, newValue);
		// Reset to first page when changing filter
		set(currentPageAtom, 1);
	},
);

// Action atom to handle registration state filter changes
export const updateRegistrationStateFilterAtom = atom(
	(get) => get(registrationStateFilterAtom),
	(get, set, newValue: string | null) => {
		set(registrationStateFilterAtom, newValue);
		// Reset to first page when changing filter
		set(currentPageAtom, 1);
	},
);

// Action atom to handle estimated retail price range changes
export const updateEstRetailPriceRangeAtom = atom(
	(get) => get(estRetailPriceRangeAtom),
	(get, set, newValue: NumericRange) => {
		set(estRetailPriceRangeAtom, newValue);
		// Reset to first page when changing filter
		set(currentPageAtom, 1);
	},
);

// Action atom to handle selected preorders changes
export const updateSelectedPreordersAtom = atom(
	(get) => get(selectedPreordersAtom),
	(get, set, newSelection: Selection) => {
		set(selectedPreordersAtom, newSelection);
		if (newSelection !== 'all') {
			set(isAllMatchingPreordersSelectedAtom, false);
		}
	},
);

// Action atom to update current preorders list
export const updateCurrentPreordersListAtom = atom(
	(get) => get(currentPreordersListAtom),
	(get, set, newList: string[]) => {
		set(currentPreordersListAtom, newList);
	},
);

// Action atom to clear all filters (excluding preorders table tab, which is controlled by tabs)
export const clearAllFiltersAtom = atom(null, (get, set) => {
	set(debouncedSearchValueAtom, '');
	set(statusFilterAtom, []);
	set(modelFilterAtom, []);
	set(orderDateFilterAtom, null);
	set(orderDateCustomRangeAtom, { from: null, to: null });
	set(preorderAmountRangeAtom, {});
	set(estRetailPriceRangeAtom, {});
	set(registrationStateFilterAtom, null);
	// Reset to first page when clearing filters
	set(currentPageAtom, 1);
});
